import { Link } from "react-router-dom";
import {
  ArrowRight, Phone, MessageCircle, Star, MapPin, ShieldCheck, Package,
  EyeOff, Scissors, PhoneCall,
  CheckCircle2, Truck, Sparkles,
} from "lucide-react";
import SectionHeading from "@/components/site/SectionHeading";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { buildWhatsAppUrl, callUrl, COMPANY } from "@/lib/config";
import { TESTIMONIALS, FAQS } from "@/lib/data";

const PILLARS = [
  { Icon: EyeOff, title: "Discretion & Safety", desc: "Background-verified crew, confidential handling of your home and belongings." },
  { Icon: Package, title: "Careful Packing & Custom Crating", desc: "Multi-layer protection with wooden crates for art, antiques and fragile pieces." },
  { Icon: ShieldCheck, title: "Transit Insurance Support", desc: "Goods-in-transit insurance available with full claim assistance on your behalf." },
];

const LUXURY_ITEMS = [
  "Fine art & paintings", "Antiques & heirlooms", "Marble & stone furniture",
  "Grand pianos", "Modular kitchens", "High-end electronics",
  "Chandeliers & light fixtures", "Designer furniture", "Wine collections",
];

const TIERS = [
  { name: "Essential", desc: "Trained crew, quality packing material, careful loading and secure transport for your routine move.", features: ["Trained uniformed crew", "Quality cartons & bubble wrap", "Closed-body vehicle", "Loading & unloading"], highlighted: false },
  { name: "Premium", desc: "Our most-chosen service. Supervised packing, custom crating where needed, dedicated coordinator through the move.", features: ["Everything in Essential", "Supervised packing team", "Custom crating for fragile pieces", "Dedicated move coordinator", "Transit insurance arranged"], highlighted: true, badge: "Most Popular" },
  { name: "Concierge", desc: "A truly hands-off relocation. We pack, transport, unpack, reassemble and set up your home or office — you walk in and live.", features: ["Everything in Premium", "On-site project manager", "Unpacking & room setup", "Furniture reassembly", "Post-move cleanup & waste removal"], highlighted: false },
];

const HOW_IT_WORKS = [
  { n: 1, title: "Consultation", desc: "A private conversation to understand your home, timelines and specific concerns." },
  { n: 2, title: "Home Survey & Written Estimate", desc: "On-site inventory assessment followed by a detailed, written proposal — no verbal promises." },
  { n: 3, title: "Professional Packing", desc: "Uniformed crew packs with multi-layer materials and custom crates for valuables." },
  { n: 4, title: "Secure Transport", desc: "Closed-body, GPS-tracked vehicles with real-time status updates to you." },
  { n: 5, title: "Unpacking & Setup", desc: "We unpack, reassemble modular furniture and set up your new space room-by-room." },
];

const PREMIUM_AREAS = [
  "Kakkanad", "Panampilly Nagar", "Marine Drive", "Edappally",
  "Willingdon Island", "Fort Kochi", "Thevara", "Aluva", "Tripunithura",
];

const RECENT_MOVES = [
  { src: "/gallery/img1.jpeg", alt: "Sofa and furniture wrapped in protective foam inside a service lift, Kochi" },
  { src: "/gallery/img2.jpeg", alt: "Household items secured with multi-layer packing for an apartment move" },
  { src: "/gallery/img3.jpeg", alt: "Move On loaded truck at an apartment pickup location in Kochi" },
  { src: "/gallery/img4.jpeg", alt: "Covered goods vehicle ready for a secure home relocation in Kerala" },
];

export default function HomePage() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-100 bg-white">
        <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] bg-[#B08D57]/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] bg-[#081537]/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#B08D57]/30 bg-[#B08D57]/5 text-[#B08D57] text-[11px] font-semibold uppercase tracking-[0.18em]">
              <Sparkles className="w-3.5 h-3.5" /> White-Glove Relocation · Kochi
            </div>
            <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#081537] leading-[1.05] text-balance">
              Executive Relocation,<br className="hidden sm:block"/> Handled with Absolute Care.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              White-glove packing, secure transport and a dedicated move specialist for homes and offices across Kochi and Kerala.
            </p>

            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/book" data-testid="hero-book-btn" className="inline-flex items-center gap-2 bg-[#081537] hover:bg-[#0F2152] text-white rounded-full px-7 py-4 font-semibold transition-all hover:-translate-y-0.5 shadow-lg shadow-blue-900/25">
                Book a Private Consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={buildWhatsAppUrl()} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn" className="inline-flex items-center gap-2 border-2 border-[#081537]/20 hover:border-[#B08D57] text-[#081537] hover:text-[#B08D57] bg-white rounded-full px-7 py-4 font-semibold transition-all">
                <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
              </a>
              <a href={callUrl} data-testid="hero-call-btn" className="md:hidden inline-flex items-center gap-2 border-2 border-[#081537] text-[#081537] hover:bg-[#081537] hover:text-white rounded-full px-7 py-4 font-semibold transition-all">
                <Phone className="w-4 h-4" /> Call Now
              </a>
            </div>
          </div>

          <div className="lg:col-span-5 animate-fade-up delay-200">
            <img src="/gallery/img3.jpeg" alt="Move On truck loaded and secured at an apartment pickup in Kochi" data-testid="hero-photo" className="w-full h-[460px] sm:h-[540px] object-cover rounded-3xl shadow-2xl shadow-blue-900/15 border border-slate-100" />
          </div>
        </div>

        {/* Trust strip */}
        <div className="relative border-t border-slate-100 bg-white/70 backdrop-blur">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <TrustStat icon={<Truck className="w-4 h-4 text-[#B08D57]" />} value="12,000+" label="moves completed" />
            <TrustStat icon={<Star className="w-4 h-4 text-[#B08D57] fill-[#B08D57]" />} value="4.9/5" label="rated by families" />
            <TrustStat icon={<CheckCircle2 className="w-4 h-4 text-[#B08D57]" />} value="2 hours" label="written estimate" />
          </div>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading overline="Our Three Pillars" title="What a premium relocation looks like." subtitle="The quiet details that separate a careful move from a careless one." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {PILLARS.map(({ Icon, title, desc }) => (
              <div key={title} data-testid={`pillar-${title.toLowerCase().replace(/\s+/g, "-").replace("&","and")}`} className="bg-white border border-slate-200 rounded-2xl p-8 hover:border-[#B08D57]/40 hover:shadow-xl hover:shadow-blue-900/5 transition-all">
                <div className="w-12 h-12 rounded-xl bg-[#081537] text-white grid place-items-center mb-6">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-serif-display text-2xl font-semibold text-[#081537]">{title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHITE-GLOVE */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHeading overline="White-Glove & Luxury Handling" title="For the pieces that matter most." subtitle="Fragile, heavy, high-value or sentimental — we treat each piece with the care it deserves, backed by the right materials and the right people." />
            <ul className="mt-10 grid grid-cols-2 gap-3">
              {LUXURY_ITEMS.map((item) => (
                <li key={item} className="flex items-center gap-2 text-slate-700 text-sm">
                  <div className="w-5 h-5 rounded-full bg-[#B08D57]/15 text-[#B08D57] grid place-items-center flex-shrink-0">
                    <CheckCircle2 className="w-3 h-3" />
                  </div>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <img src="/gallery/img1.jpeg" alt="Furniture wrapped in multi-layer protective foam during a Move On packing job" data-testid="white-glove-photo" className="w-full h-[460px] object-cover rounded-3xl shadow-xl shadow-blue-900/10 border border-slate-100" />
        </div>
      </section>

      {/* CHOOSE YOUR MOVE */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading overline="Choose Your Move" title="Three service levels. One standard of care." align="center" subtitle="From a routine shift to a fully managed, white-glove relocation. Each tier is quoted individually after a home survey." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {TIERS.map((t) => (
              <div key={t.name} data-testid={`tier-${t.name.toLowerCase()}`} className={`relative rounded-2xl p-8 transition-all ${t.highlighted ? "bg-[#081537] text-white border-2 border-[#B08D57] shadow-2xl shadow-blue-900/20 lg:-translate-y-4" : "bg-white border border-slate-200 hover:border-[#B08D57]/40 hover:shadow-xl"}`}>
                {t.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#B08D57] text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">{t.badge}</div>
                )}
                <h3 className={`font-serif-display text-2xl font-bold ${t.highlighted ? "text-white" : "text-[#081537]"}`}>{t.name}</h3>
                <p className={`mt-3 text-sm leading-relaxed ${t.highlighted ? "text-slate-300" : "text-slate-600"}`}>{t.desc}</p>
                <ul className="mt-6 space-y-2.5">
                  {t.features.map((f) => (
                    <li key={f} className={`flex items-start gap-2 text-sm ${t.highlighted ? "text-slate-200" : "text-slate-700"}`}>
                      <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${t.highlighted ? "text-[#B08D57]" : "text-[#081537]"}`} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/book" data-testid={`tier-${t.name.toLowerCase()}-cta`} className={`mt-8 inline-flex items-center justify-center gap-2 w-full rounded-full py-3 font-semibold transition ${t.highlighted ? "bg-[#B08D57] hover:bg-[#8d6e42] text-white" : "bg-[#081537] hover:bg-[#0F2152] text-white"}`}>
                  Request Consultation <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSURANCE BANNER */}
      <section className="py-10 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#B08D57]/10 text-[#B08D57] grid place-items-center flex-shrink-0">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <div className="font-serif-display text-xl sm:text-2xl font-semibold text-[#081537]">Goods-in-transit insurance available, with claim support.</div>
              <div className="text-sm text-slate-600">We arrange cover appropriate to your inventory and guide any claim end-to-end.</div>
            </div>
          </div>
          <Link to="/contact" data-testid="insurance-cta" className="whitespace-nowrap inline-flex items-center gap-2 text-sm font-semibold text-[#081537] hover:text-[#B08D57]">
            Learn More <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading overline="How It Works" title="Five steps, no surprises." subtitle="A clear path from first conversation to the moment you unlock the door of your new home." />
          <ol className="mt-14 grid grid-cols-1 md:grid-cols-5 gap-5">
            {HOW_IT_WORKS.map((s) => (
              <li key={s.n} data-testid={`how-step-${s.n}`} className="relative bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#B08D57]/40 transition-all">
                <div className="font-serif-display text-5xl font-bold text-[#B08D57]/20">{String(s.n).padStart(2, "0")}</div>
                <div className="font-serif-display text-lg font-semibold text-[#081537] mt-2">{s.title}</div>
                <div className="text-sm text-slate-600 mt-2 leading-relaxed">{s.desc}</div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* TESTIMONIALS (existing only) */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading overline="In Their Words" title="What our families say." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.slice(0, 6).map((t) => (
              <div key={t.name} data-testid={`testimonial-${t.name.toLowerCase().replace(/\s+/g, "-")}`} className="bg-slate-50 rounded-2xl p-7 border border-slate-100">
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#B08D57] text-[#B08D57]" />)}
                </div>
                <p className="text-slate-700 leading-relaxed">&ldquo;{t.text}&rdquo;</p>
                <div className="mt-5 pt-5 border-t border-slate-200 flex items-center gap-3">
                  <img src={t.avatar} alt={`${t.name} — customer of Move On`} className="w-11 h-11 rounded-full object-cover" />
                  <div>
                    <div className="font-semibold text-[#081537] text-sm">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.location}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <a
              href="https://www.google.com/search?q=Move+On+Packers+%26+Movers+Kochi+reviews"
              target="_blank"
              rel="noreferrer"
              data-testid="google-reviews-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#081537] hover:text-[#B08D57]"
            >
              See our Google reviews <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading overline="Service Areas" title="Serving Kochi's finest neighbourhoods." subtitle="From heritage homes to the city's newest gated communities — we know the roads, the societies and the lifts." />
          <div className="mt-12 flex flex-wrap gap-3">
            {PREMIUM_AREAS.map((a) => (
              <div key={a} data-testid={`area-${a.toLowerCase().replace(/\s+/g, "-")}`} className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-[#B08D57] rounded-full px-5 py-3 text-sm font-medium text-slate-700 hover:text-[#B08D57] transition cursor-pointer">
                <MapPin className="w-4 h-4 text-[#B08D57]" /> {a}
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/service-areas" className="text-sm font-semibold text-[#081537] hover:text-[#B08D57] inline-flex items-center gap-1">
              View all 80+ localities we serve <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading overline="FAQ" title="Frequently asked questions." />
          <Accordion type="single" collapsible className="mt-10" data-testid="home-faq">
            {FAQS.slice(0, 6).map((f, idx) => (
              <AccordionItem key={idx} value={`item-${idx}`} className="border-b border-slate-200">
                <AccordionTrigger className="text-left text-base font-semibold text-[#081537] hover:text-[#B08D57] py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-8 text-center">
            <Link to="/faq" className="text-sm font-semibold text-[#B08D57] hover:text-[#8d6e42] inline-flex items-center gap-1">
              See all FAQs <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        {/* FAQPage schema (visible content above) */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQS.slice(0, 6).map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        })}} />
      </section>

      {/* RECENT MOVES */}
      <section className="py-20 bg-slate-50 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading overline="From Recent Moves" title="Real jobs. Real care." subtitle="A look at our recent packing and transport work across Kochi — every item wrapped, secured and delivered as packed." />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {RECENT_MOVES.map((p) => (
              <img key={p.src} src={p.src} alt={p.alt} data-testid={`recent-move-${p.src.split("/").pop().split(".")[0]}`} className="w-full h-56 object-cover rounded-2xl border border-slate-200 hover:shadow-xl hover:shadow-blue-900/10 transition-all" />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Strip */}
      <section className="py-16 bg-[#081537]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-serif-display text-2xl sm:text-3xl font-bold text-white">Ready to plan a move you won&rsquo;t have to worry about?</h3>
            <p className="mt-2 text-slate-300">Request a private consultation. Written estimate in 2 hours.</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <a href={callUrl} className="bg-[#B08D57] hover:bg-[#8d6e42] text-white rounded-full px-6 py-3 font-semibold inline-flex items-center gap-2 transition" data-testid="cta-call-btn">
              <Phone className="w-4 h-4" /> {COMPANY.phone}
            </a>
            <Link to="/book" className="bg-white text-[#081537] rounded-full px-6 py-3 font-semibold hover:bg-slate-100 transition" data-testid="cta-book-btn">
              Book a Private Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---------- Helpers ---------- */
function TrustStat({ icon, value, label }) {
  return (
    <div className="flex items-center gap-3 justify-center sm:justify-start">
      <div className="w-9 h-9 rounded-full bg-[#B08D57]/10 grid place-items-center">{icon}</div>
      <div>
        <div className="font-serif-display text-lg font-bold text-[#081537] leading-none">{value}</div>
        <div className="text-xs text-slate-500 mt-0.5">{label}</div>
      </div>
    </div>
  );
}
